import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  ChevronLeft,
  Info,
  QrCode,
  Clock,
  Home,
  Briefcase,
  UserPlus,
  ClipboardList,
} from "lucide-react";
import { useCommunity } from "@/contexts/CommunityContext";

const categoryLabels = {
  TEMPORARY: "Temporal",
  PERMANENT: "Permanente",
  SERVICE: "Servicio",
};

const categoryIcons = {
  TEMPORARY: Clock,
  PERMANENT: Home,
  SERVICE: Briefcase,
};

const statusColors = {
  PENDING: "secondary",
  ACTIVE: "default",
  REVOKED: "destructive",
  EXPIRED: "outline",
} as const;

const statusLabels = {
  PENDING: "Pendiente",
  ACTIVE: "Activa",
  REVOKED: "Revocada",
  EXPIRED: "Vencida",
};

function formatDate(date: Date | string) {
  return new Date(date).toLocaleDateString("es-AR", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

type InvitationCategory = "TEMPORARY" | "PERMANENT" | "SERVICE";
type InvitationStatus = "PENDING" | "ACTIVE" | "REVOKED" | "EXPIRED";

export function ManageInvitationsPage() {
  const { selectedCommunityId } = useCommunity();
  const [categoryFilter, setCategoryFilter] = useState<
    InvitationCategory | "ALL"
  >("ALL");
  const [statusFilter, setStatusFilter] = useState<InvitationStatus | "ALL">(
    "ALL",
  );

  const { data: adminCommunities, isLoading: adminCommunitiesLoading } =
    trpc.amenity.getAdminCommunities.useQuery();

  const hasAdminAccess =
    selectedCommunityId &&
    adminCommunities?.some((c) => c.id === selectedCommunityId);

  const { data: invitations, isLoading: invitationsLoading } =
    trpc.invitation.getAllForCommunity.useQuery(
      {
        communityId: selectedCommunityId || "",
        ...(categoryFilter !== "ALL" && { category: categoryFilter }),
        ...(statusFilter !== "ALL" && { status: statusFilter }),
      },
      { enabled: !!selectedCommunityId && !!hasAdminAccess },
    );

  // Loading
  if (adminCommunitiesLoading) {
    return (
      <div className="container mx-auto max-w-6xl px-4 py-6 md:px-6 md:py-10">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  // No admin access
  if (!hasAdminAccess) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-6 md:px-6 md:py-10">
        <div className="mb-8">
          <Link
            to="/invitations"
            className="mb-4 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Volver a invitaciones
          </Link>
          <h1 className="text-2xl font-bold md:text-3xl">
            Gestionar invitaciones
          </h1>
        </div>

        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            No tenés permisos de administración para gestionar invitaciones en
            este barrio.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-6xl px-4 py-6 md:px-6 md:py-10">
      <div className="mb-8">
        <Link
          to="/invitations"
          className="mb-4 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="mr-1 h-4 w-4" />
          Volver a invitaciones
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold md:text-3xl">
              Gestionar invitaciones
            </h1>
            <p className="text-muted-foreground">
              Mirá todas las invitaciones de tu barrio.
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link to="/invitations/access-log">
              <ClipboardList className="mr-2 h-4 w-4" />
              Registro de acceso
            </Link>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="w-48">
              <Select
                value={categoryFilter}
                onValueChange={(v) =>
                  setCategoryFilter(v as InvitationCategory | "ALL")
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Categoría" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todas las categorías</SelectItem>
                  <SelectItem value="TEMPORARY">Temporal</SelectItem>
                  <SelectItem value="PERMANENT">Permanente</SelectItem>
                  <SelectItem value="SERVICE">Servicio</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Select
                value={statusFilter}
                onValueChange={(v) =>
                  setStatusFilter(v as InvitationStatus | "ALL")
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos los estados</SelectItem>
                  <SelectItem value="PENDING">Pendiente</SelectItem>
                  <SelectItem value="ACTIVE">Activa</SelectItem>
                  <SelectItem value="REVOKED">Revocada</SelectItem>
                  <SelectItem value="EXPIRED">Vencida</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invitations List */}
      {invitationsLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : invitations && invitations.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y">
              {invitations.map((invitation) => {
                const CategoryIcon = categoryIcons[invitation.category];

                return (
                  <div
                    key={invitation.id}
                    className="flex items-center justify-between p-4"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted">
                        {invitation.status === "PENDING" ? (
                          <UserPlus className="h-5 w-5" />
                        ) : (
                          <CategoryIcon className="h-5 w-5" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            {invitation.guestName ||
                              "Datos del invitado pendientes"}
                          </span>
                          <Badge
                            variant={statusColors[invitation.status]}
                            className="text-xs"
                          >
                            {statusLabels[invitation.status]}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {categoryLabels[invitation.category]}
                          </Badge>
                        </div>
                        <div className="mt-1 flex items-center gap-3 text-sm text-muted-foreground">
                          <span>
                            {invitation.unit.blockSection
                              ? `${invitation.unit.blockSection} - ${invitation.unit.unitNumber}`
                              : invitation.unit.unitNumber}
                          </span>
                          <span>•</span>
                          <span>
                            Por{" "}
                            {invitation.createdBy.name ||
                              invitation.createdBy.email}
                          </span>
                          <span>•</span>
                          <span>{formatDate(invitation.createdAt)}</span>
                        </div>
                        {invitation.guestEmail && (
                          <div className="mt-1 text-sm text-muted-foreground">
                            {invitation.guestEmail}
                            {invitation.guestDni &&
                              ` • DNI: ${invitation.guestDni}`}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right text-sm text-muted-foreground">
                      {invitation.category === "PERMANENT" ? (
                        <span>Desde {formatDate(invitation.validFrom)}</span>
                      ) : (
                        <span>
                          {formatDate(invitation.validFrom)} -{" "}
                          {invitation.validUntil
                            ? formatDate(invitation.validUntil)
                            : "Sin vencimiento"}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <QrCode className="mx-auto h-12 w-12 text-muted-foreground" />
            <p className="mt-4 text-muted-foreground">
              No se encontraron invitaciones con esos filtros.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
